<script lang="ts" module>
  import type { Filter, FilterReference, SortOption } from '../../../stores';
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { fly } from 'svelte/transition';
  import { Section } from '@agapi/ui/section';
  import { Select, SelectControl, SelectContent, SelectItem, type SelectRootProps } from '@agapi/ui/select';
  import { Button } from '@agapi/ui/button';
  import { stack } from '@agapi/ui/panda/patterns';
  import { css } from '@agapi/ui/panda/css';

  import { PriceFilter } from '../../../components/price-filter';
  import SelectFilter from '../../select-filter/select-filter.svelte';
  import { useCollectionContext } from '../../../contexts';
  import { getLocaleFromUrl } from '../../../utils';
  import { isMobile } from '../../../utils/device.client';

  import * as m from '$lib/parachute/messages';

  const locale = getLocaleFromUrl(page.url);

  const collectionStore = useCollectionContext();

  let selectedSortOption = $derived<SortOption>(collectionStore.content.sortBy ?? collectionStore.sortOptions.default);
  let debouncer = $state<NodeJS.Timeout | null>(null);

  const distinctValueChangeHandler = (details: Parameters<NonNullable<SelectRootProps['onValueChange']>>[0], filter: Filter & { type: 'distinct' }) => {
    collectionStore.load({ facetFilter: { key: filter.label, type: filter.type, values: details.value } });
  }

  const rangeValueChangeHandler = async (details: number[]): Promise<void> => {
    if (debouncer) {
      clearTimeout(debouncer);
    }

    debouncer = setTimeout(async () => {
      const [ min, max ] = details;

      const values = {
        ...(min ? { min: Math.floor(min * 100) } : {}),
        ...(max ? { max: Math.ceil(max * 100) } : {})
      }

      await collectionStore.load({
        facetFilter: {
          key: `price_amount_${locale.country}`,
          type: 'range',
          values: values
        }
      });
    }, 300);
  }

  const adjustLabelForMobile = (filter: Filter & { type: 'distinct' }) => {
    return filter.items.map((item) => {
      const label = item.label.split('>').pop()?.trim() ?? item.label;

      return {
        ...item,
        label
      }
    });
  }
</script>

<Section rootStyles={css.raw({ pt: '0', pb: '0' })} containerStyles={css.raw({ gap: '0' })}>
  <!-- No need to show filter if there is only one product displaying -->
  {#if (collectionStore.content.products.length === 1 && collectionStore.loading) || collectionStore.content.products.length > 1 || collectionStore.isFilteredOrSorted}
    <div
      class={stack({
        mt: '4',
        flexDirection: 'row',
        smDown: {
          maskImage: 'linear-gradient(to right, transparent, black 30px, black calc(100% - 30px), transparent)',
          mx: '-app',
          px: 'app',
          overflowX: 'scroll',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }
      })}
    >
      {#each collectionStore.filters as filter}
        {@const key = filter.label}
        {@const values = collectionStore.content.filtersSelected[key]?.values as string[]}

        {#if filter.type === 'distinct'}
          <SelectFilter
            bind:this={collectionStore.filterReferences[key] as FilterReference}
            label={filter.label}
            items={isMobile() ? adjustLabelForMobile(filter) : filter.items}
            values={values}
            onValueChange={(details) => distinctValueChangeHandler(details, filter)}
          />
        {:else if filter.type === 'range'}
          {@const min = filter.min}
          {@const max = filter.max}
          {@const minScope = filter.selectedMin ?? filter.min}
          {@const maxScope = filter.selectedMax ?? filter.max}
          {@const selectedMin = minScope < min ? min : minScope}
          {@const selectedMax = maxScope > max ? max : maxScope}

          <PriceFilter
            bind:this={collectionStore.filterReferences[key] as FilterReference}
            min={Math.floor(min / 100)}
            max={Math.ceil(max / 100)}
            value={[Math.floor(selectedMin / 100), Math.ceil(selectedMax / 100)]}
            onValueChange={rangeValueChangeHandler}
          />
        {/if}
      {/each}
    </div>
  {/if}

  <div
    class={stack({
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      mt: '4',
      mb: '4'
    })}
  >
    <span class={css({ fontSize: 'sm', color: 'text.subdued', sm: { fontSize: 'md' } })}>
      {m.displayingXOfYProducts({ value: collectionStore.content.products.length, total: collectionStore.content.count })}
    </span>

    <div class={stack({ flexDirection: 'row', alignItems: 'center', gap: '5' })}>
      {#if collectionStore.isFilteredOrSorted}
        <span transition:fly={{ x: -2, duration: 200 }}>
          <Button
            variant="text"
            css={{
              textDecoration: 'underline'
            }}
            onclick={() => collectionStore.reset()}
          >
            {m.clearFilters()}
          </Button>
        </span>
      {/if}

      {#key collectionStore.content.products.length === 1 || collectionStore.isSorted || selectedSortOption}
        <Select
          bind:this={collectionStore.sortReference}
          value={[selectedSortOption.value]}
          items={collectionStore.sortOptions.options}
          disabled={collectionStore.content.products.length === 1}
          onValueChange={(details) => collectionStore.load({ sortBy: details.items?.[0] as SortOption })}
        >
          <SelectControl
            triggerLabel={selectedSortOption.label}
            buttonStyles={{ borderRadius: 'unset', border: 'unset', borderBottom: '1px solid', fontWeight: 'bold' }}
            labelStyles={collectionStore.isSorted ? css.raw({
              borderRadius: 'var(--radii-full)',
              fontWeight: 'bold',
              background: 'surface.primary.subdued.background',
              color: 'surface.primary.subdued.foreground',
              paddingInline: 'var(--spacing-3)'
            }) : undefined}
          />

          <SelectContent contentStyles={{ zIndex: 5 }}>
            {#each collectionStore.sortOptions.options as option}
              <SelectItem item={option} />
            {/each}
          </SelectContent>
        </Select>
      {/key}
    </div>
  </div>
</Section>
